import type { FC, MutableRefObject } from "react";
import { useEffect, useState } from "react";

import type { Team } from "@smartsuite/automation-engine-api";
import { TextEditorDescription_EmojiType } from "@smartsuite/automation-engine-api";
import {
  AvatarMenuOption,
  DropdownPanel,
  MenuGroupContent,
  MenuOption,
  MenuOptionWrapper,
} from "@smartsuite/react-ui/lib";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { useTheme } from "../../../../../../../../stores";
import type {
  DynamicSection,
  DynamicSectionOption,
  DynamicSectionOptionGroup,
} from "../../../../../../../../types/line";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { SlackEmojiOptions } from "../SlackEmojiOptions";
import { TriggerOptions } from "../TriggerOptions";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { ActionOptions } from "../ActionOptions";
import { SubMenuOptions } from "../SubMenuOptions";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../CurrentOption";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { ifLoopingSourceIsActionFind } from "../../fields/utils";
import { createLoopItemPill } from "../../../../utils/createLoopItemPill";
import { nameToInitials } from "../../../../utils";
import { StyledNoFields } from "../TriggerOptions/style";
import type { ActionCandidate } from "../../../../../../../../types/candidate";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";
import { resolveEditorLoopScope } from "../../../../utils/action-sources";

export enum Tab {
  ROOT = "root",
  TRIGGER_FIELDS = "trigger-fields",
  ACTION_FIELDS = "action-fields",
  LOOPING_FIELDS = "current-item",
  SLACK_EMOJI = "slack-emoji",
  SUB_MENU = "sub-menu",
  CURRENT_ITEM = "current-item",
  TEAMS = "teams",
}
const TriggerDropdown: FC<{
  isOpen: boolean;
  onClose: (event: any) => void;
  addPill: (attrs: PillNodeAttrs) => void;
  buttonRef: MutableRefObject<HTMLElement>;
  actionMenus: DynamicSection[];
  triggerMenu: DynamicSection | null;
  loopingMenu: DynamicSection | null;
  /** See CurrentOption: the loop a branch condition belongs to, overriding
   * the store's selected-container locator (BUG-21770). */
  loopScope?: LoopScope;
  actionsArePreScoped?: boolean;
  hideCurrentItem?: boolean;
  onChange: (input: string) => void;
  showEmoji: TextEditorDescription_EmojiType;
  onCloseAnimationEnd: () => void;
  showTeams?: boolean;
  teams?: Team[];
  selectedTeamIds?: string[];
  onSelectTeam?: (team: Team) => void;
  action?: ActionCandidate;
}> = ({
  isOpen,
  onClose,
  addPill,
  buttonRef,
  actionMenus,
  triggerMenu,
  loopingMenu,
  loopScope,
  actionsArePreScoped,
  showEmoji,
  onChange,
  onCloseAnimationEnd,
  hideCurrentItem,
  showTeams = false,
  teams = [],
  selectedTeamIds = [],
  onSelectTeam,
  action,
}) => {
  const theme = useTheme();
  const store = useRootStore();
  const [tab, setTab] = useState<Tab>(Tab.ROOT);
  const [prevTab, setPrevTab] = useState<Tab>(Tab.ROOT);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedActionMenu, setSelectedActionMenu] = useState<
    DynamicSection | undefined
  >();
  const [selectedActionHash, setSelectedActionHash] = useState<string>();
  const [subOptions, setSubOptions] = useState<DynamicSectionOptionGroup>({
    options: [],
    label: "",
  });
  const rootLooping = store.automations.candidate.looping;
  const looping = resolveEditorLoopScope(
    action,
    rootLooping,
    loopScope,
    actionsArePreScoped,
  );

  useEffect(() => {
    switchTab(Tab.TRIGGER_FIELDS);
  }, [triggerMenu]);

  const triggerOutputs = triggerMenu?.options ?? [];
  const filteredTeams = teams
    .filter((team) => !selectedTeamIds.includes(team.teamId))
    .filter((team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

  const goSubMenu = (options: DynamicSectionOption[], label: string): void => {
    setSubOptions({ options, label });
    switchTab(Tab.SUB_MENU);
  };

  const switchTab = (newTab: Tab): void => {
    setPrevTab(tab);
    setTab(newTab);
  };

  return (
    <DropdownPanel
      targetElementRef={buttonRef}
      isActive={isOpen}
      at={POSITION_AT}
      my={POSITION_MY}
      minWidth={300}
      arrowBackground="custom"
      arrowCustomBackground={SolutionColorVariables.sc1}
      panelLineBackground={SolutionColorVariables.sc1}
      portalStyles={getLocalCssColorVariables(theme)}
      onClickOutside={onClose}
      onCloseAnimationEnd={() => {
        setSearchQuery("");
        onCloseAnimationEnd();
      }}
    >
      {tab === Tab.ROOT && (
        <MenuOptionWrapper>
          {triggerOutputs.length > 0 && (
            <MenuOption
              submenuArrow="right"
              onClick={() => switchTab(Tab.TRIGGER_FIELDS)}
            >
              {t("automation-form.fields-from-trigger")}
            </MenuOption>
          )}
          {actionMenus.map(
            (actionMenu) =>
              actionMenu?.options.length > 0 && (
                <MenuOption
                  key={actionMenu?.hash}
                  submenuArrow="right"
                  onClick={() => {
                    setSelectedActionHash(actionMenu?.hash);
                    setSelectedActionMenu(actionMenu);
                    switchTab(Tab.ACTION_FIELDS);
                  }}
                >
                  {actionMenu.hideNamePrefix === true
                    ? actionMenu.name
                    : t("field-popups.action-popup", {
                        name: actionMenu.name,
                      })}
                </MenuOption>
              ),
          )}
          {!hideCurrentItem && (
            <CurrentOption
              loopingMenu={loopingMenu}
              loopScope={looping}
              actionsArePreScoped={actionsArePreScoped}
              addPill={(looping) => {
                // Case when source for looping is the find action
                if (
                  ifLoopingSourceIsActionFind(looping) ||
                  loopingMenu?.options?.length
                ) {
                  switchTab(Tab.CURRENT_ITEM);
                  return;
                }

                if (looping.source) {
                  const loopPill = createLoopItemPill(looping);
                  if (loopPill) {
                    addPill(loopPill);
                  }
                }
              }}
            />
          )}
          {showEmoji === TextEditorDescription_EmojiType.SLACK && (
            <MenuOption
              submenuArrow="right"
              onClick={() => {
                switchTab(Tab.SLACK_EMOJI);
              }}
            >
              {t("field-popups.emoji-popup")}
            </MenuOption>
          )}
          {showTeams && teams.length > 0 && onSelectTeam !== undefined && (
            <MenuOption
              submenuArrow="right"
              onClick={() => {
                setSearchQuery("");
                switchTab(Tab.TEAMS);
              }}
            >
              {t("editor.teams")}
            </MenuOption>
          )}
        </MenuOptionWrapper>
      )}
      {tab === "trigger-fields" && (
        <TriggerOptions
          tab={tab}
          searchPlaceholder={"Search Fields"}
          title={t("automation-form.fields-from-trigger")}
          onNavigateBack={() => switchTab(Tab.ROOT)}
          addPill={addPill}
          options={triggerOutputs}
          hasSingleMenuItem={false}
          goSubMenu={goSubMenu}
        />
      )}
      {tab === "current-item" && loopingMenu && (
        <TriggerOptions
          tab={tab}
          searchPlaceholder={"Search Fields"}
          title={loopingMenu.name}
          onNavigateBack={() => switchTab(Tab.ROOT)}
          addPill={addPill}
          options={loopingMenu.options}
          hasSingleMenuItem={false}
          goSubMenu={goSubMenu}
          loopingMenuHash={loopingMenu.hash}
          loopingMenu={loopingMenu}
        />
      )}
      {tab === "action-fields" && (
        <ActionOptions
          onNavigateBack={() => switchTab(Tab.ROOT)}
          addPill={addPill}
          selectedActionMenu={selectedActionMenu}
          selectedActionHash={selectedActionHash}
          goSubMenu={goSubMenu}
        />
      )}
      {tab === "slack-emoji" && (
        <SlackEmojiOptions
          onNavigateBack={() => switchTab(Tab.ROOT)}
          onChange={onChange}
        />
      )}
      {tab === "teams" && showTeams && onSelectTeam !== undefined && (
        <MenuGroupContent
          title={t("editor.teams")}
          onSearchChange={(query) => setSearchQuery(query)}
          onNavigateBack={() => {
            setSearchQuery("");
            switchTab(Tab.ROOT);
          }}
        >
          {filteredTeams.map((team) => (
            <AvatarMenuOption
              key={team.teamId}
              onClick={() => onSelectTeam(team)}
              initials={nameToInitials(team.name)}
            >
              {team.name}
            </AvatarMenuOption>
          ))}
          {filteredTeams.length === 0 && (
            <StyledNoFields>No results</StyledNoFields>
          )}
        </MenuGroupContent>
      )}
      {tab === "sub-menu" && (
        <SubMenuOptions
          title={subOptions.label}
          prevTab={prevTab}
          hasSingleMenuItem={
            triggerOutputs.length === 0 && actionMenus.length === 0
          }
          onNavigateBack={() => switchTab(prevTab)}
          addPill={addPill}
          options={subOptions.options}
          selectedActionHash={selectedActionHash}
          loopingMenuHash={loopingMenu?.hash}
        />
      )}
    </DropdownPanel>
  );
};

export { TriggerDropdown };
